import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { Heart, Shield, Users, Sparkles } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/about/")({
  head: () => {
    const title = "About LetsListBusiness — Our Mission & Story";
    const description = "Learn how LetsListBusiness helps communities discover and support independent local businesses through real recommendations.";
    const url = SITE_URL + "/about/";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "About LetsListBusiness" },
        { property: "og:description", content: "Our mission to support local." },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "About", href: "/about/" },
        ], SITE_URL),
      ],
    };
  },
  component: AboutPage,
});

function AboutPage() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "About" }]} />

      {/* Hero Section — with generated image */}
      <section className="container mx-auto px-4 pb-16 pt-8">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">About LetsListBusiness</div>
            <h1 className="mt-6 font-display text-5xl font-bold leading-[1.02] sm:text-7xl">
              Surfacing the soul of your <em className="not-italic text-primary">neighborhood.</em>
            </h1>
            <p className="mt-8 text-xl leading-relaxed text-muted-foreground">
              We started LetsListBusiness with a simple observation: the best businesses in a city aren't always the ones with the biggest marketing budgets or the most aggressive SEO. They're the ones built on craft, community, and word-of-mouth.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/add-business" className="rounded-full bg-foreground px-8 py-4 text-sm font-bold text-background transition-transform hover:scale-105">
                List your business
              </Link>
              <Link to="/contact" className="rounded-full border border-border px-8 py-4 text-sm font-bold transition-colors hover:bg-muted">
                Get in touch
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-4 rounded-4xl bg-gradient-warm opacity-20 blur-3xl" />
            <img 
              src="https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?auto=format&fit=crop&w=800&q=60" 
              alt="Vibrant community illustration" 
              className="relative aspect-square w-full rounded-3xl object-cover shadow-2xl"
            />
          </div>
        </div>
      </section>

      {/* The Problem & The Solution */}
      <section className="bg-surface py-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="font-display text-3xl font-bold sm:text-4xl">The Internet got big, but we stayed local.</h2>
            <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
              The modern web is crowded with paid placements and generic recommendations. We believe that finding a reliable plumber or a hidden-gem café shouldn't feel like navigating a minefield of ads. That's why we built a directory that prioritizes human connection over algorithmic efficiency.
            </p>
          </div>
          <div className="mt-16 grid gap-8 md:grid-cols-3">
            {[
              {
                title: "The Problem",
                text: "Global platforms often miss the nuances of local communities, surfacing only the loudest voices.",
                color: "bg-red-500/10 text-red-600 border-red-500/20"
              },
              {
                title: "Our Mission",
                text: "To bridge the gap between residents and the independent businesses that make their cities unique.",
                color: "bg-primary/10 text-primary border-primary/20"
              },
              {
                title: "The Result",
                text: "A curated space where trust is the primary currency and everyone has a seat at the table.",
                color: "bg-green-500/10 text-green-600 border-green-500/20"
              }
            ].map((box) => (
              <div key={box.title} className={`rounded-3xl border p-8 ${box.color}`}>
                <h3 className="font-display text-xl font-bold">{box.title}</h3>
                <p className="mt-4 leading-relaxed opacity-90">{box.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values & Stats Grid */}
      <section className="container mx-auto grid gap-16 px-4 py-24 lg:grid-cols-2">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Our Core Values</div>
          <h2 className="mt-4 font-display text-3xl font-bold">What drives us every single day.</h2>
          <div className="mt-10 space-y-6">
            {[
              { icon: Heart, title: "Radical Transparency", text: "We share our methods and never hide behind black-box algorithms." },
              { icon: Shield, title: "Zero Noise Policy", text: "If it's not a real recommendation from a real person, it's not on LetsListBusiness." },
              { icon: Users, title: "Shared Ownership", text: "We consider our community of reviewers as partners in our growth." },
              { icon: Sparkles, title: "Slow Growth", text: "We'd rather cover one city perfectly than a thousand cities poorly." },
            ].map((v) => (
              <div key={v.title} className="flex gap-5">
                <span className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-surface border border-border text-primary shadow-sm">
                  <v.icon className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display text-lg font-semibold">{v.title}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{v.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-[2.5rem] bg-foreground p-10 text-background sm:p-16">
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-foreground/60">LetsListBusiness in Numbers</div>
          <div className="mt-10 grid gap-10 sm:grid-cols-2">
            {[
              { v: "120K+", l: "Active reviewers" },
              { v: "10K+", l: "Verified listings" },
              { v: "6", l: "Cities covered" },
              { v: "4.7★", l: "Average rating" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-5xl font-bold tracking-tight">{s.v}</div>
                <div className="mt-2 text-sm font-medium text-background/60">{s.l}</div>
              </div>
            ))}
          </div>
          <div className="mt-12 h-px bg-background/10" />
          <p className="mt-8 text-lg font-medium leading-relaxed italic opacity-80">
            "We aren't trying to be everything for everyone. We're trying to be the most trusted resource for the places you call home."
          </p>
        </div>
      </section>

      {/* Timeline */}
      <section className="bg-surface py-24">
        <div className="container mx-auto px-4">
          <div className="grid gap-12 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Our Journey</div>
              <h2 className="mt-4 font-display text-4xl font-bold leading-tight">A few years of building local, slowly.</h2>
              <p className="mt-6 text-muted-foreground">
                We believe that trust takes time to build. Here's a look at the milestones that brought us to where we are today.
              </p>
            </div>
            <div className="lg:col-span-8">
              <ol className="relative space-y-12 before:absolute before:left-[11px] before:top-2 before:h-[calc(100%-16px)] before:w-0.5 before:bg-border sm:before:left-[15px]">
                {[
                  { year: "2022", title: "The Weekend Project", text: "LetsListBusiness was born in a garage in Austin, TX, originally created to help neighbors find reliable home service providers without the stress of anonymous reviews." },
                  { year: "2023", title: "The City Expansion", text: "After a successful pilot, we expanded to cover restaurants, cafés, and wellness centers in Portland and Denver, focusing on high-quality, verified listings." },
                  { year: "2024", title: "Verified Community", text: "We launched our signature 'Verified Reviewer' program and hired a small editorial team of local experts to provide deep-dive guides for every city." },
                  { year: "2025", title: "The 10K Milestone", text: "Today, we cover 6 major metros and host over 10,000 hand-picked listings. We're expanding carefully, ensuring our standards never slip." },
                ].map((m) => (
                  <li key={m.year} className="relative pl-10 sm:pl-12">
                    <span className="absolute left-0 top-1.5 h-[24px] w-[24px] rounded-full border-4 border-background bg-primary sm:h-[32px] sm:w-[32px]" />
                    <div className="font-mono text-sm font-bold text-primary">{m.year}</div>
                    <h3 className="mt-1 font-display text-xl font-bold">{m.title}</h3>
                    <p className="mt-2 text-lg leading-relaxed text-foreground/80">{m.text}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-24">
        <div className="relative overflow-hidden rounded-[3rem] bg-foreground px-8 py-20 text-center text-background sm:px-16 sm:py-32">
          <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/20 blur-3xl" />
          <div className="absolute -bottom-20 -left-20 h-64 w-64 rounded-full bg-gradient-warm opacity-30 blur-3xl" />
          
          <h2 className="relative mx-auto max-w-2xl font-display text-4xl font-bold sm:text-6xl">Ready to discover your neighborhood?</h2>
          <p className="relative mx-auto mt-6 max-w-xl text-lg text-background/70 sm:text-xl">
            Whether you're looking for a hidden gem or want to put your business on the map, we're here to help you connect with your community.
          </p>
          <div className="relative mt-12 flex flex-wrap justify-center gap-4">
            <Link to="/" className="rounded-full bg-primary px-10 py-5 text-sm font-bold text-primary-foreground transition-transform hover:scale-105">
              Explore listings
            </Link>
            <Link to="/add-business" className="rounded-full bg-surface/10 px-10 py-5 text-sm font-bold backdrop-blur-sm transition-colors hover:bg-surface/20">
              Add your business
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
